import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { StatCard } from "@/components/StatCard";
import { yen } from "@/lib/format";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { NAV } from "@/components/Sidebar";
import {
  Building2, Home, Users, DoorOpen, Percent, Receipt, Wallet, AlertTriangle,
  Landmark, Wrench, TrendingDown, TrendingUp, FileClock, HardHat, Bell, Loader2, ShieldCheck, CreditCard,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar,
} from "recharts";

function Section({ title, children }) {
  const { t } = useI18n();
  return (
    <div className="mb-8">
      <h2 className="text-xs uppercase tracking-[0.1em] font-medium text-muted-foreground mb-3">{t(title)}</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">{children}</div>
    </div>
  );
}

function flattenNav() {
  const out = [];
  NAV.forEach((item) => {
    if (item.to && item.to !== "/dashboard") {
      out.push({ to: item.to, label: item.label, icon: item.icon });
    }
    if (item.children) {
      item.children.forEach((c) => out.push({ to: c.to, label: c.label, icon: c.icon }));
    }
  });
  return out;
}

function MenuGrid() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const items = flattenNav();
  const [counts, setCounts] = useState({});

  useEffect(() => {
    api.get("/dashboard/menu-counts").then(({ data }) => setCounts(data)).catch(() => {});
  }, []);

  return (
    <div className="mb-8">
      <h2 className="text-xs uppercase tracking-[0.1em] font-medium text-muted-foreground mb-3">{t("クイックメニュー")}</h2>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2.5 md:gap-3">
        {items.map((it) => {
          const count = counts[it.to];
          return (
            <button
              key={it.to}
              onClick={() => navigate(it.to)}
              data-testid={`menu-card-${it.to}`}
              className="group relative flex flex-col items-center justify-center gap-2 bg-white border border-border rounded-sm p-3 md:p-4 transition-colors hover:border-brand/60 hover:bg-gray-50/50"
            >
              {count != null && (
                <span
                  className="absolute top-1.5 right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-gray-100 group-hover:bg-brand/10 text-[11px] font-medium text-gray-600 group-hover:text-brand flex items-center justify-center transition-colors"
                  data-testid={`menu-count-${it.to}`}
                >
                  {count > 999 ? "999+" : count}
                </span>
              )}
              <span className="w-9 h-9 md:w-10 md:h-10 rounded-sm bg-brand/10 flex items-center justify-center group-hover:bg-brand/20 transition-colors">
                <it.icon className="w-[18px] h-[18px] text-brand transition-colors" />
              </span>
              <span className="text-[11px] md:text-xs text-center leading-tight text-[#0A0A0A]">{t(it.label)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [series, setSeries] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/dashboard/cashflow").then(({ data }) => setSeries(data)).catch(() => {});
  }, []);

  if (user?.role === "system_admin") {
    const saasItems = [
      { to: "/admin/companies", label: "運営管理", icon: ShieldCheck },
      { to: "/settings/billing", label: "プラン・お支払い", icon: CreditCard },
      { to: "/settings/users", label: "ユーザー", icon: Users },
      { to: "/settings/roles", label: "権限", icon: ShieldCheck },
      { to: "/settings/emails", label: "メール通知", icon: Bell },
      { to: "/settings/audit", label: "監査ログ", icon: FileClock },
    ];
    return (
      <div className="animate-fade-in" data-testid="saas-dashboard">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-medium tracking-tight">{t("ダッシュボード")}</h1>
          <p className="text-sm text-muted-foreground mt-1">SaaS 運営管理</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {saasItems.map((it) => (
            <button key={it.to} onClick={() => navigate(it.to)} data-testid={`saas-card-${it.to}`}
              className="group flex flex-col items-center justify-center gap-2 bg-white border border-border rounded-sm p-5 transition-colors hover:border-brand/60 hover:bg-gray-50/50">
              <span className="w-10 h-10 rounded-sm bg-brand/10 flex items-center justify-center group-hover:bg-brand/20 transition-colors"><it.icon className="w-5 h-5 text-brand" /></span>
              <span className="text-xs text-center text-[#0A0A0A]">{t(it.label)}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (!stats) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;
  }

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-medium tracking-tight">{t("ダッシュボード")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{stats.year_month} {t("経営サマリー")}</p>
      </div>

      <MenuGrid />

      <Section title="ポートフォリオ概況">
        <StatCard label="所有物件数" value={stats.owned_properties} unit="件" icon={Building2} onClick={() => navigate("/properties")} testid="stat-owned" />
        <StatCard label="管理物件数" value={stats.managed_properties} unit="件" icon={Home} onClick={() => navigate("/properties")} testid="stat-managed" />
        <StatCard label="総戸数" value={stats.total_units} unit="戸" icon={DoorOpen} onClick={() => navigate("/rooms")} testid="stat-units" />
        <StatCard label="入居戸数" value={stats.occupied_units} unit="戸" icon={Users} onClick={() => navigate("/rooms")} testid="stat-occupied" />
        <StatCard label="空室数" value={stats.vacant_units} unit="戸" icon={DoorOpen} tone="amber" onClick={() => navigate("/vacancies")} testid="stat-vacant" />
        <StatCard label="入居率" value={stats.occupancy_rate} format="pct" tone="brand" icon={Percent} testid="stat-occupancy" />
      </Section>

      <Section title="今月の収支">
        <StatCard label="今月家賃請求額" value={stats.month_billing} format="yen" icon={Receipt} onClick={() => navigate("/invoices")} testid="stat-billing" />
        <StatCard label="今月入金額" value={stats.month_payments} format="yen" tone="green" icon={Wallet} onClick={() => navigate("/payments")} testid="stat-payments" />
        <StatCard label="未収金" value={stats.receivables} format="yen" tone="red" icon={AlertTriangle} onClick={() => navigate("/arrears")} testid="stat-receivables" />
        <StatCard label="今月ローン返済額" value={stats.month_loan} format="yen" icon={Landmark} onClick={() => navigate("/loans")} testid="stat-loan" />
        <StatCard label="今月修繕費" value={stats.month_repair} format="yen" icon={Wrench} onClick={() => navigate("/repairs")} testid="stat-repair" />
        <StatCard label="今月経費" value={stats.month_expense} format="yen" icon={TrendingDown} onClick={() => navigate("/finance")} testid="stat-expense" />
        <StatCard label="今月キャッシュフロー" value={stats.month_cashflow} format="yen" tone={stats.month_cashflow >= 0 ? "green" : "red"} icon={TrendingUp} onClick={() => navigate("/finance")} testid="stat-cashflow" />
      </Section>

      <Section title="対応が必要な項目 / アラート">
        <StatCard label="滞納件数" value={stats.arrears_count} unit="件" tone="red" icon={AlertTriangle} onClick={() => navigate("/arrears")} testid="stat-arrears" />
        <StatCard label="契約更新予定" value={stats.renewals_due} unit="件" tone="amber" icon={FileClock} onClick={() => navigate("/contracts")} testid="stat-renewals" />
        <StatCard label="修繕対応中" value={stats.repairs_in_progress} unit="件" icon={HardHat} onClick={() => navigate("/repairs")} testid="stat-repairs-progress" />
        <StatCard label="重要アラート" value={stats.alerts} unit="件" tone="brand" icon={Bell} onClick={() => navigate("/notifications")} testid="stat-alerts" />
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        <div className="lg:col-span-2 bg-white border border-border rounded-sm p-5">
          <h3 className="text-sm font-medium mb-4">{t("今月キャッシュフロー")}（{new Date().getFullYear()}）</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={series} margin={{ left: -10, right: 10, top: 4 }}>
              <defs>
                <linearGradient id="cf" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FF5A00" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#FF5A00" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#999" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#999" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${Math.round(v / 10000)}万`} />
              <Tooltip formatter={(v) => yen(v)} contentStyle={{ borderRadius: 4, border: "1px solid #eee", fontSize: 12 }} />
              <Area type="monotone" dataKey="cashflow" stroke="#FF5A00" strokeWidth={2} fill="url(#cf)" name="CF" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-border rounded-sm p-5">
          <h3 className="text-sm font-medium mb-4">{t("請求")} vs {t("入金")}</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={series} margin={{ left: -10, right: 10, top: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#999" }} axisLine={false} tickLine={false} interval={1} />
              <YAxis tick={{ fontSize: 11, fill: "#999" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${Math.round(v / 10000)}万`} />
              <Tooltip formatter={(v) => yen(v)} contentStyle={{ borderRadius: 4, border: "1px solid #eee", fontSize: 12 }} />
              <Bar dataKey="billing" fill="#111111" name="請求" radius={[2, 2, 0, 0]} />
              <Bar dataKey="income" fill="#FF5A00" name="入金" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
